package com.arena_web.ui.components.cards

import androidx.compose.runtime.Composable
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

// Translucent dark pill behind the "PRO+" label — keeps the chip legible over
// any card art without hiding the avatar underneath.
private const val PRO_PILL_BG = "rgba(11, 22, 46, 0.72)"

/**
 * Gated-card treatment for a card whose owner has no active subscription.
 * Renders as an absolutely-positioned overlay filling its (relative) parent —
 * a single "PRO+" chip pinned to the top-right corner. The card frame and the
 * real generated avatar underneath stay fully visible (no dimming / frost);
 * the chip alone communicates that renewing the subscription is required.
 *
 * The overlay is `pointer-events: none` so the parent's click handler (which
 * should open the paywall) still fires when a gated card is tapped.
 *
 * @param compact Shrinks the chip for small gallery thumbnails.
 */
@Composable
fun FrozenCardOverlay(compact: Boolean = false) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("pointer-events", "none")
            },
        contentAlignment = Alignment.TopEnd,
    ) {
        Box(
            modifier = Modifier
                .sMargin(if (compact) 6 else 10)
                .sBorderRadius(100)
                .sPadding(if (compact) 3 else 4, if (compact) 8 else 10)
                .styleModifier {
                    property("background", PRO_PILL_BG)
                    property("border", "0.3px solid var(--arena-border-top)")
                    property("backdrop-filter", "blur(4px)")
                    property("-webkit-backdrop-filter", "blur(4px)")
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                t.cardProPlus,
                modifier = Modifier
                    .sFontSize(if (compact) 10 else 12)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(if (compact) 12 else 14)
                    .sLetterSpacing(0.02)
                    .color(com.arena_web.ui.theme.ThemeVars.Text1),
            )
        }
        // Centered padlock (mobile parity) — makes the gate unmistakable at a
        // glance; the PRO+ chip alone reads too much like a plain badge.
        Box(
            modifier = Modifier
                .sSize(if (compact) 32 else 40)
                .sBorderRadius(100)
                .styleModifier {
                    property("position", "absolute")
                    property("top", "50%")
                    property("left", "50%")
                    property("transform", "translate(-50%, -50%)")
                    property("background", PRO_PILL_BG)
                    property("border", "0.3px solid var(--arena-border-top)")
                    property("backdrop-filter", "blur(4px)")
                    property("-webkit-backdrop-filter", "blur(4px)")
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText("🔒", modifier = Modifier.sFontSize(if (compact) 14 else 18))
        }
    }
}
